import { ExpandMoreOutlined, LanguageOutlined } from '@mui/icons-material'
import type { AccordionProps } from '@mui/material'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  alpha,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  Stack,
  Typography,
} from '@mui/material'
import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchResourceWithPost } from '@app/src/api/fetchHooks'
import { useDrawer } from '@app/src/components/Drawer/DrawerContext'
import DrawerViewLinkProviderCandidates from '@app/src/components/Drawer/Views/DrawerViewLinkProviderCandidates'
import CompanyCell from '@app/src/components/Table/Cells/CompanyCell'
import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'
import type { Provider, ProviderOrganizationSimilarityResult } from '@app/src/types/organizations'
import { ProviderLinkStatus } from '@app/src/types/organizations'
import { HALF_MINUTE_MS } from '@app/src/wf-constants'

type ExistingProvidersAccordionProps = {
  providers: Array<Provider>
  selectedProviderIds: Array<number>
  onChange: (providerId: number) => void
} & Omit<AccordionProps, 'onChange' | 'children'>

const ExistingProvidersAccordion: React.FC<ExistingProvidersAccordionProps> = ({
  providers,
  selectedProviderIds,
  onChange,
  ...props
}) => {
  const { formatMessage } = useIntl()
  const { openDrawer } = useDrawer()

  const providerIds = useMemo(() => providers.map(provider => provider.id), [providers])

  const { data: existingOrganizations = [], isLoading: isLoadingExisting } = useFetchResourceWithPost<
    Array<ProviderOrganizationSimilarityResult>
  >({
    endpoint: endpoints.bulkFindOrganizations,
    key: FetchKey.ReferralContactExists,
    body: providerIds,
    options: {
      enabled: Boolean(providerIds.length),
      staleTime: HALF_MINUTE_MS,
    },
  })

  const providerMatches = useMemo(
    () =>
      providers
        .map(provider => ({
          provider,
          organizations:
            existingOrganizations
              .find(item => item.providerId === provider.id)
              ?.organizations.map(
                orgSimilarity =>
                  ({
                    ...orgSimilarity.organization,
                    alreadyLinkedOrganization: false,
                    linked: false,
                    linkStatus: ProviderLinkStatus.Created,
                    targetOrganizationId: provider.id,
                    organization: orgSimilarity.organization,
                    organizationId: orgSimilarity.organization.id,
                    type: provider.type,
                  }) as Provider,
              ) ?? [],
        }))
        .filter(match => match.organizations.length > 0)
        .sort((a, b) => a.organizations.length - b.organizations.length),
    [providers, existingOrganizations],
  )

  if (!providerIds.length || (!isLoadingExisting && !existingOrganizations.length)) {
    return null
  }

  return (
    <Accordion
      elevation={0}
      disableGutters
      {...props}
      sx={[{ bgcolor: 'grey.100', '&:before': { position: 'unset' } }, ...[props.sx ?? []].flat()]}
    >
      <AccordionSummary
        expandIcon={
          isLoadingExisting ? (
            <CircularProgress size={16} sx={{ mx: 1.5 }} />
          ) : (
            <ExpandMoreOutlined fontSize="medium" sx={{ mx: 1 }} />
          )
        }
        sx={{ py: 1 }}
        disabled={isLoadingExisting}
      >
        <Stack direction="row">
          <LanguageOutlined color="secondary" />
          <Typography variant="body1" ml={3}>
            {isLoadingExisting
              ? formatMessage({ id: 'form.connectProviders.review.loadingExisting' })
              : formatMessage({ id: 'form.connectProviders.review.mayExist' }, { count: existingOrganizations.length })}
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>
        <List disablePadding>
          {providerMatches.map(match => (
            <ListItem
              key={`provider-${match.provider.id}`}
              sx={{
                justifyContent: 'space-between',
                borderTop: `1px solid ${alpha('#000', 0.12)}`,
              }}
              disablePadding
            >
              <ListItemButton
                selected={selectedProviderIds.includes(match.provider.id)}
                onClick={() => onChange(match.provider.id)}
              >
                <Box display="flex" justifyContent="space-between" width="100%">
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Checkbox
                      edge="start"
                      checked={selectedProviderIds.includes(match.provider.id)}
                      onChange={() => onChange(match.provider.id)}
                    />
                    <CompanyCell company={match.provider} disableCell />
                  </Stack>

                  <Button
                    sx={{ minWidth: 'auto', marginLeft: 0 }}
                    onClick={e => {
                      e.stopPropagation()
                      openDrawer(
                        <DrawerViewLinkProviderCandidates
                          resource={match.provider}
                          organizationCandidates={match.organizations}
                          hideNewCompany
                        />,
                        { showAboveDialogs: true },
                      )
                    }}
                  >
                    {formatMessage(
                      { id: 'form.connectProviders.review.viewMore' },
                      {
                        count: match.organizations.length,
                      },
                    )}
                  </Button>
                </Box>
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </AccordionDetails>
    </Accordion>
  )
}

export default ExistingProvidersAccordion
